import React, { type PropsWithChildren } from 'react';
import { useLocation } from 'react-router';
import classNames from 'classnames';

import type { PromotedEntities, UnionsMenuLayerItem } from '../../types';
import { MENU_ITEM_TYPE } from 'app/modules/Navigation/types';

import { getSportIconUrl } from 'app/lib/betting/sport-utils';
import { getImageUrlFromCdn } from 'app/lib/compose-image-src';
import { getUniversalUrl } from 'app/lib/get-universal-url';

import { PrematchTournamentsMenuLayer } from '../../submodules/PrematchMenu/layers/Tournaments';
import { createMenuItemTestDataAttributesGetter } from '../../test-data-helpers';
import { isSelectedMenuItem } from '../../utils';
import { MenuItem } from '../MenuItem';
import { LinkContent } from './LinkContent';

import styles from './UnionsMenu.module.scss';

const getMenuItemTestDataAttributes = createMenuItemTestDataAttributesGetter(
  'layer-menu-item',
  MENU_ITEM_TYPE.UNION,
);

type UnionMenuItemProps = {
  promotedEntities: PromotedEntities;
  promotedEntityId: string;
  entry: UnionsMenuLayerItem;
  className?: string;
};

export const UnionMenuItem: React.FC<PropsWithChildren<UnionMenuItemProps>> = ({
  entry,
  promotedEntities,
  promotedEntityId,
  children,
}) => {
  const { pathname } = useLocation();
  const testDataAttributes = getMenuItemTestDataAttributes(entry);
  const isSelectedUnion = isSelectedMenuItem(entry, pathname);
  const iconPatch = entry.entity.icon;

  const iconURL = iconPatch
    ? getImageUrlFromCdn(iconPatch, 24, 'webp', 'CDN_FOR_TOURNAMENT_ICONS_URL')
    : getUniversalUrl(getSportIconUrl());

  const linkClassNames = classNames(
    styles.link,
    {
      [styles.linkSelected]: isSelectedUnion,
    },
  );

  const linkContent = (
    <LinkContent caption={entry.caption}
                 label={entry.label}
                 iconURL={iconURL}>
      {children}
    </LinkContent>
  );

  const innerList = isSelectedUnion
    ? <PrematchTournamentsMenuLayer promotedEntities={promotedEntities} />
    : undefined;

  return (
    <MenuItem entry={entry}
              promotedEntityId={promotedEntityId}
              promotedEntities={promotedEntities}
              testDataAttributes={testDataAttributes}
              to={entry.location}
              classNameItem={styles.item}
              classNameLink={linkClassNames}
              innerList={innerList}>
      {linkContent}
    </MenuItem>
  );
};
